import ObjectScreen from '@components/v2/layout/ObjectScreen/ObjectScreen';
import Icon from '@components/v2/ui/Icon/Icon';
import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import { Box } from '@mui/material';
import { CircleAlert } from 'lucide-react';
import LangEnum from '../../../../../../../../lang/LangEnum';
import { OrderObjectTestIds } from '../../../../../../../../utils/testIds/OrderObject.testIds';
import { UsedInOfflineMode } from '../../../../../../../offline/types/UsedInOfflineMode';
import { useOrderEditSectionsContext } from '../../../../../../edit/sections/OrderEditSections.context';
import OrderValidationErrorsTooltipContent from '../../../../../../edit/validate/components/OrderValidationErrorsTooltipContent';
import { Order } from '../../../../../../order.types';
import OrderGeneralDataFields from './components/OrderGeneralDataFields';
import OrderPriceTableConflictModal from './components/OrderPriceTableConflictModal';
import useOrderObjectGeneralDataSection from './hooks/useOrderObjectGeneralDataSection';

interface OrderObjectGeneralDataSectionProps extends UsedInOfflineMode {
  order: Order;
  isReadOnlyView: boolean;
}

const OrderObjectGeneralDataSection = ({
  order,
  isReadOnlyView,
  isOfflineMode,
}: OrderObjectGeneralDataSectionProps) => {
  const {
    canEditOrder,
    permissions,
    disabledFields,
    isConsultantVisible,
    sources,
    fetchers,
    priceTableConflictModal,
    confirmPriceTableUpdate,
    submit,
  } = useOrderObjectGeneralDataSection({ order, isReadOnlyView, isOfflineMode });
  const { hasGeneralDataErrors, generalDataErrors, rawGeneralDataErrors } =
    useOrderEditSectionsContext();

  return (
    <>
      <ObjectScreen.Section
        title={LangEnum.GLOBAL_GENERAL_DATA}
        persistKey="order:general-data"
        isDefaultOpen
        alert={
          hasGeneralDataErrors ? (
            <Tooltip
              TooltipContent={<OrderValidationErrorsTooltipContent errors={generalDataErrors} />}
            >
              <Box
                test-id={OrderObjectTestIds.GENERAL_DATA_ERROR_ICON}
                sx={{ display: 'inline-flex', color: 'error.main' }}
              >
                <Icon icon={CircleAlert} size="sm" />
              </Box>
            </Tooltip>
          ) : undefined
        }
      >
        <OrderGeneralDataFields
          order={order}
          canEditOrder={canEditOrder}
          permissions={permissions}
          disabledFields={disabledFields}
          isConsultantVisible={isConsultantVisible}
          sources={sources}
          fetchers={fetchers}
          generalDataErrors={rawGeneralDataErrors}
          submit={submit}
        />
      </ObjectScreen.Section>

      <OrderPriceTableConflictModal
        isOpen={priceTableConflictModal.isOpen}
        onClose={priceTableConflictModal.closeModal}
        onConfirm={confirmPriceTableUpdate}
      />
    </>
  );
};

export default OrderObjectGeneralDataSection;
